import type {
  ApiSequenceConfig,
  ApiSequenceResult,
  ApiSequenceTriggeredVia,
} from '@chatsdet/types';
import { API_URL } from '../../../../../config';
import { toApiError } from '../../../../../lib/api-error';

export type ApiSequenceDto = {
  id: string;
  workflowId: string;
  orgId: string;
  credentialId: string | null;
  config: ApiSequenceConfig;
  status: 'draft' | 'ready' | 'stale';
  guidancePrompt: string | null;
  sourceRunId: string | null;
  generatedAt: string;
  createdAt: string;
  updatedAt: string;
};

export type ApiSequenceRunDto = {
  id: string;
  apiSequenceId: string;
  orgId: string;
  status: 'queued' | 'running' | 'success' | 'failed' | 'canceled';
  result: ApiSequenceResult | null;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  triggeredBy: string | null;
  triggeredVia: ApiSequenceTriggeredVia | null;
  workflowRunId: string | null;
  /** Set when the run originated from a test suite (joined from workflow_test_runs). */
  workflowTestRunId: string | null;
  createdAt: string;
};

export type ApiSequenceFetch = (
  input: string | URL | Request,
  init?: RequestInit
) => Promise<Response>;

export type ApiSequenceRequestOptions = {
  apiUrl?: string;
  fetchFn?: ApiSequenceFetch;
};

export type SaveApiSequenceInput = {
  workflowId: string;
  credentialId?: string | null;
  config: ApiSequenceConfig;
  sourceRunId?: string | null;
  guidancePrompt?: string | null;
};

export type UpdateApiSequenceConfigInput = {
  apiSequenceId: string;
  config: ApiSequenceConfig;
};

const defaultFetch: ApiSequenceFetch = (input, init) => fetch(input, init);

const hasText = (value: string | null | undefined): boolean =>
  typeof value === 'string' && value.trim().length > 0;

const hasConfiguredDraftStepFields = (step: ApiSequenceConfig['steps'][number]): boolean => {
  if (step.credentialId !== null && step.credentialId !== undefined) return true;
  if (hasText(step.credentialTemplateId)) return true;
  if (hasText(step.credentialTemplateName)) return true;
  if (hasText(step.method)) return true;
  if (hasText(step.urlTemplate)) return true;
  if (hasText(step.bodyTemplate)) return true;
  if (hasText(step.responseVariableName)) return true;
  if (hasText(step.description)) return true;
  if ((step.expectedStatusCodes?.length ?? 0) > 0) return true;
  if ((step.validations?.length ?? 0) > 0) return true;
  if ((step.queryParams ?? []).some((param) => hasText(param.key) || hasText(param.value))) {
    return true;
  }
  if (
    Object.entries(step.headersTemplate ?? {}).some(
      ([key, value]) => hasText(key) || hasText(value)
    )
  ) {
    return true;
  }
  if (
    (step.formParts ?? []).some(
      (part) =>
        hasText(part.name) ||
        (part.kind === 'file' && hasText(part.attachmentId)) ||
        (part.kind === 'text' && hasText(part.value))
    )
  ) {
    return true;
  }
  return false;
};

export function sanitizeApiSequenceConfigForSave(config: ApiSequenceConfig): ApiSequenceConfig {
  const completeSteps = config.steps
    .filter((step) => step.apiRequestId.trim().length > 0 || hasConfiguredDraftStepFields(step))
    .map((step) => ({
      ...step,
      apiRequestId: step.apiRequestId.trim(),
    }));
  const retainedStepIds = new Set(
    completeSteps.map((step, index) => step.stepId ?? `step${index + 1}`)
  );

  const exports = config.exports;
  const sanitizedConfig: ApiSequenceConfig = { ...config, steps: completeSteps };
  if (!exports) return sanitizedConfig;

  const completeExports = exports
    .filter(
      (item) =>
        item.variableName.trim().length > 0 &&
        item.stepId.trim().length > 0 &&
        item.responsePath.trim().length > 0 &&
        retainedStepIds.has(item.stepId.trim())
    )
    .map((item) => ({
      ...item,
      variableName: item.variableName.trim(),
      stepId: item.stepId.trim(),
      responsePath: item.responsePath.trim(),
    }));

  if (completeExports.length === 0) {
    delete sanitizedConfig.exports;
  } else {
    sanitizedConfig.exports = completeExports;
  }
  return sanitizedConfig;
}

export async function saveApiSequenceRequest(
  data: SaveApiSequenceInput,
  options: ApiSequenceRequestOptions = {}
): Promise<{ ok: boolean; data: ApiSequenceDto }> {
  const fetchFn = options.fetchFn ?? defaultFetch;
  const apiUrl = options.apiUrl ?? API_URL;
  const payload: SaveApiSequenceInput = {
    ...data,
    config: sanitizeApiSequenceConfigForSave(data.config),
  };
  const response = await fetchFn(`${apiUrl}/api-sequences/save`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw await toApiError(response, 'Failed to save API sequence');
  return response.json() as Promise<{ ok: boolean; data: ApiSequenceDto }>;
}

export async function updateApiSequenceConfigRequest(
  data: UpdateApiSequenceConfigInput,
  options: ApiSequenceRequestOptions = {}
): Promise<{ ok: boolean }> {
  const fetchFn = options.fetchFn ?? defaultFetch;
  const apiUrl = options.apiUrl ?? API_URL;
  const response = await fetchFn(`${apiUrl}/api-sequences/${data.apiSequenceId}/config`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ config: sanitizeApiSequenceConfigForSave(data.config) }),
  });
  if (!response.ok) throw await toApiError(response, 'Failed to update API sequence config');
  return response.json() as Promise<{ ok: boolean }>;
}
